/**
 * Shared formatting primitives for the signatures feature.
 *
 * Single source of truth for how values are rendered in the **template editor
 * preview** AND the **sent-document stamping / preview**. Divergence between
 * these two was causing the realtor to design against one format and then see
 * a different one on the final PDF.
 *
 * All functions here are pure and locale-aware. Formatters cache Intl objects
 * at module scope so calling them in a hot render path (field overlay) is cheap.
 */

import { getLocalPhoneNumberIfIsraeliNumber } from "../utils/Haim/phoneNumbersUtils";
import type {
  SignaturePrefillSource,
  UserInfoBlockConfig,
  UserInfoBlockLineKey
} from "../@types/sharedSchema";
import { USER_INFO_BLOCK_LINE_KEYS } from "../@types/sharedSchema";

// ============================================================================
// Price (ILS)
// ============================================================================

const ilsFormatter = new Intl.NumberFormat("he-IL", {
  style: "currency",
  currency: "ILS",
  maximumFractionDigits: 0
});

export function formatPriceIls(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return "";
  return ilsFormatter.format(price);
}

// ============================================================================
// Phone (local Israeli format)
// ============================================================================

/**
 * Renders a phone number in the local Israeli national format (e.g.
 * "050-123-4567"). Non-Israeli numbers come back in E.164. Unparseable input
 * falls through to the raw string so we never blank-out a value the realtor
 * can still read — the stamp just shows what was stored.
 */
export function formatPhoneLocal(phone: string | null | undefined): string {
  if (!phone) return "";
  const local = getLocalPhoneNumberIfIsraeliNumber(phone, false);
  return local || phone;
}

// ============================================================================
// Date
// ============================================================================

/**
 * Supported date formats for template date fields. Stored on the field so
 * the realtor can pick "what does a date look like on this document" at
 * design time and have it rendered identically in editor + preview + stamp.
 */
export const DateFormats = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "DD.MM.YYYY",
  "long"
] as const;
export type DateFormat = typeof DateFormats[number];

export const DEFAULT_DATE_FORMAT: DateFormat = "DD/MM/YYYY";

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);

const longFormatterHe = new Intl.DateTimeFormat("he-IL", {
  year: "numeric",
  month: "long",
  day: "numeric"
});

export function formatDate(
  date: Date | string | number | null | undefined,
  format: DateFormat = DEFAULT_DATE_FORMAT
): string {
  if (date == null) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";

  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());

  switch (format) {
    case "DD/MM/YYYY":
      return `${dd}/${mm}/${yyyy}`;
    case "MM/DD/YYYY":
      return `${mm}/${dd}/${yyyy}`;
    case "YYYY-MM-DD":
      return `${yyyy}-${mm}-${dd}`;
    case "DD-MM-YYYY":
      return `${dd}-${mm}-${yyyy}`;
    case "DD.MM.YYYY":
      return `${dd}.${mm}.${yyyy}`;
    case "long":
      return longFormatterHe.format(d);
    default:
      return `${dd}/${mm}/${yyyy}`;
  }
}

export function formatToday(format: DateFormat = DEFAULT_DATE_FORMAT): string {
  return formatDate(new Date(), format);
}

// ============================================================================
// Listing address
// ============================================================================

type AddressFragments = {
  street?: string | null;
  houseNumber?: string | number | null;
  city?: string | null;
} | null | undefined;

export function formatListingAddress(address: AddressFragments): string {
  if (!address) return "";
  const street = [address.street, address.houseNumber]
    .filter((v) => v !== undefined && v !== null && v !== "")
    .join(" ");
  return [street, address.city].filter(Boolean).join(", ") || "";
}

type FullAddressFragments = {
  street?: string | null;
  houseNumber?: string | number | null;
  apartmentNumber?: string | number | null;
  floor?: string | number | null;
  neighborhood?: string | null;
  city?: string | null;
} | null | undefined;

/**
 * The FULL listing address — everything we know: street + house number,
 * apartment number, floor, neighborhood, city. This is what the
 * hidden-addresses flow discloses in the post-signature addendum (and the
 * identification caption in the send form's override card).
 */
export function formatFullListingAddress(address: FullAddressFragments): string {
  if (!address) return "";
  const streetPart = [address.street, address.houseNumber]
    .filter((v) => v !== undefined && v !== null && v !== "")
    .join(" ");
  const apartmentPart =
    address.apartmentNumber !== undefined &&
    address.apartmentNumber !== null &&
    address.apartmentNumber !== ""
      ? `דירה ${address.apartmentNumber}`
      : "";
  const floorPart =
    address.floor !== undefined && address.floor !== null && address.floor !== ""
      ? `קומה ${address.floor}`
      : "";
  const neighborhoodPart = address.neighborhood ? `שכונת ${address.neighborhood}` : "";
  return [streetPart, apartmentPart, floorPart, neighborhoodPart, address.city]
    .filter(Boolean)
    .join(", ") || "";
}

/**
 * The address as it should read INSIDE a contract: street + house number,
 * apartment number, floor, city. Sits between {@link formatListingAddress}
 * (street + city only — too vague to identify a flat in a tower) and
 * {@link formatFullListingAddress} (adds the neighborhood, which is noise
 * once you have an exact street number and is only wanted by the
 * hidden-addresses addendum).
 *
 * This is what the send form renders into the listings table, which is why
 * the form insists the underlying parts are actually filled in.
 */
export function formatContractListingAddress(address: FullAddressFragments): string {
  if (!address) return "";
  const streetPart = [address.street, address.houseNumber]
    .filter((v) => v !== undefined && v !== null && v !== "")
    .join(" ");
  const apartmentPart =
    address.apartmentNumber !== undefined &&
    address.apartmentNumber !== null &&
    address.apartmentNumber !== ""
      ? `דירה ${address.apartmentNumber}`
      : "";
  const floorPart =
    address.floor !== undefined && address.floor !== null && address.floor !== ""
      ? `קומה ${address.floor}`
      : "";
  return [streetPart, apartmentPart, floorPart, address.city].filter(Boolean).join(", ") || "";
}

/**
 * Redacted address for hidden-addresses documents — same shape as the full
 * address but with the house number and floor withheld, and EVERY digit
 * stripped from the result as an extra safety net (a street name like
 * "העצמאות 12" pasted into the street field can't leak the number). The
 * send form pre-fills this as the default; the realtor can override it per
 * listing per send.
 */
export function formatRedactedListingAddress(address: FullAddressFragments): string {
  if (!address) return "";
  const neighborhoodPart = address.neighborhood ? `שכונת ${address.neighborhood}` : "";
  const joined = [address.street, neighborhoodPart, address.city].filter(Boolean).join(", ");
  // Strip anything numeric, then tidy the separators the removal left behind.
  return joined
    .replace(/\d+/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+,/g, ",")
    .replace(/,\s*(?=,)/g, "")
    .replace(/^[\s,]+|[\s,]+$/g, "");
}

// ============================================================================
// Contact-field label captions
// ============================================================================

/**
 * Friendly Hebrew name for a prefilled field, used by the editor as the
 * placeholder text inside an unfilled overlay box ("שם הלקוח" instead of
 * the internal `contact_name`). Returns an empty string when the source
 * doesn't have a friendly name configured — callers should fall back to
 * the field's internal `name` only as a last resort.
 *
 * Hardcoded Hebrew on purpose: document content is Hebrew across the
 * product, and these strings double as the editor's own "what is this
 * field" label.
 */
export function getFieldFriendlyName(
  key: SignaturePrefillSource | undefined | null
): string {
  if (!key || key === "none") return "";
  switch (key) {
    case "contact.name":
      return "שם הלקוח";
    case "contact.phone":
      return "טלפון הלקוח";
    case "contact.email":
      return "מייל הלקוח";
    case "contact.id_number":
      return "ת.ז הלקוח";
    case "contact.additional_phones":
      return "טלפונים נוספים";
    case "contact.address":
      return "כתובת הלקוח";
    case "listing.address":
      return "כתובת הנכס";
    case "listing.price":
      return "מחיר הנכס";
    case "listing.rooms":
      return "מספר חדרים";
    case "listing.square_meter":
      return 'מ"ר';
    case "realtor.name":
      return "שם המתווך";
    case "realtor.phone":
      return "טלפון המתווך";
    case "realtor.email":
      return "מייל המתווך";
    case "realtor.company":
      return "חברת המתווך";
    case "realtor.license":
      return "מס' רישיון";
    case "realtor.company_id":
      return "ח.פ/עוסק מורשה";
    case "realtor.company_logo":
      return "לוגו חברה";
    case "realtor.signature_image":
      return "חתימת המתווך";
    case "realtor.user_info_block":
      return "פרטי המתווך";
    case "document.formatted_number":
      return "מספר מסמך";
    case "today":
      return "תאריך היום";
    case "signing_date":
      return "תאריך חתימה";
    default:
      return "";
  }
}

/**
 * Hebrew caption shown next to a contact-detail field on the stamped PDF
 * (and in the editor preview) when the realtor has turned on "show label"
 * for that field. Empty string for sources that don't have a built-in
 * caption — callers should treat empty as "no label, skip stamping".
 *
 * Hardcoded Hebrew because document content is Hebrew across the product
 * (matches the listings-block column headers, etc.). i18n on stamped output
 * would be a separate, broader change.
 */
export function getFieldLabelText(
  key: SignaturePrefillSource | undefined | null
): string {
  if (!key) return "";
  switch (key) {
    case "contact.name":
      return "שם הלקוח:";
    case "contact.phone":
      return "טלפון:";
    case "contact.email":
      return "מייל:";
    case "contact.id_number":
      return "ת.ז:";
    case "contact.additional_phones":
      return "טלפונים נוספים:";
    case "signing_date":
      return "תאריך חתימה:";
    case "contact.address":
      return "כתובת:";
    case "realtor.name":
      return "המתווך:";
    case "realtor.phone":
      return "טלפון המתווך:";
    case "realtor.company":
      return "חברת המתווך:";
    case "realtor.company_id":
      return "ח.פ:";
    case "realtor.license":
      return "מס' רישיון תיווך:";
    default:
      return "";
  }
}

// ============================================================================
// Realtor user-info block
// ============================================================================

/**
 * Renders the realtor's profile fields as a multi-line block ready to be
 * stamped at the top of a document. Each line's inclusion + prefix label is
 * controlled by `config` (per-field on the template); when `config` is
 * absent every line defaults to enabled with its built-in default label.
 * Lines whose underlying user field is empty are silently skipped so the
 * block stays clean (e.g. a realtor without a registered ח.פ won't show
 * "ח.פ:" with nothing after it).
 *
 * Returns an empty string when nothing renders so callers can decide to
 * skip stamping entirely.
 */
export type RealtorUserInfoSource = {
  ownerFullName?: string | null;
  ownerPhoneNumber?: string | null;
  realtorLicense?: string | null;
  companyId?: string | null;
  companyName?: string | null;
};

/** Built-in per-line prefix label. Empty string means "no prefix". */
export const DEFAULT_USER_INFO_BLOCK_LABELS: Record<UserInfoBlockLineKey, string> = {
  name: "",
  license: "רישיון תיווך מס': ",
  phone: "טלפון: ",
  companyId: "ח.פ: ",
  company: "משרד: "
};

/** Resolve a single line's value from the realtor profile. Phone is local-formatted. */
export function getUserInfoBlockValue(
  user: RealtorUserInfoSource | null | undefined,
  key: UserInfoBlockLineKey
): string {
  if (!user) return "";
  switch (key) {
    case "name":
      return user.ownerFullName || "";
    case "license":
      return user.realtorLicense || "";
    case "phone":
      return user.ownerPhoneNumber ? formatPhoneLocal(user.ownerPhoneNumber) : "";
    case "companyId":
      return user.companyId || "";
    case "company":
      return user.companyName || "";
    default:
      return "";
  }
}

export function isUserInfoBlockLineEnabled(
  config: UserInfoBlockConfig | undefined,
  key: UserInfoBlockLineKey
): boolean {
  return config?.[key]?.enabled ?? true;
}

export function getUserInfoBlockLineLabel(
  config: UserInfoBlockConfig | undefined,
  key: UserInfoBlockLineKey
): string {
  const custom = config?.[key]?.customLabel;
  return custom !== undefined ? custom : DEFAULT_USER_INFO_BLOCK_LABELS[key];
}

export function buildUserInfoBlockText(
  user: RealtorUserInfoSource | null | undefined,
  config?: UserInfoBlockConfig
): string {
  if (!user) return "";
  const lines: string[] = [];
  for (const key of USER_INFO_BLOCK_LINE_KEYS) {
    if (!isUserInfoBlockLineEnabled(config, key)) continue;
    const value = getUserInfoBlockValue(user, key);
    if (!value) continue;
    const label = getUserInfoBlockLineLabel(config, key);
    lines.push(`${label}${value}`);
  }
  return lines.join("\n");
}

// ============================================================================
// Sample values (for editor overlay previews)
// ============================================================================

/**
 * Sample values shown in the template editor overlay when a field's real
 * value isn't resolvable at design time (e.g. contact.price — there's no
 * contact picked yet). Uses the shared formatters so what the realtor sees
 * in the editor matches the send-time render pixel-for-pixel.
 *
 * Kept in this module (next to the formatters) so all "how a field looks"
 * decisions live in one file.
 */
export const SAMPLE_CONTACT_NAME = "ישראל ישראלי";
export const SAMPLE_CONTACT_PHONE = "+972541234567";
export const SAMPLE_CONTACT_EMAIL = "example@webot.co.il";
export const SAMPLE_CONTACT_ID_NUMBER = "123456789";
export const SAMPLE_LISTING_ADDRESS = "דיזנגוף 99, תל אביב";
export const SAMPLE_LISTING_PRICE = 1_200_000;
export const SAMPLE_LISTING_ROOMS = 3;
export const SAMPLE_LISTING_SQM = 85;
export const SAMPLE_DOCUMENT_NUMBER = "DOC-001";
